import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import { Formik } from "formik";
import { Modal } from "@material-ui/core";
import { CustomFileUpload } from "../../../common/FormInputs";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getFileName } from "../../../../helpers/functions";
import { s3Upload } from "../../../common/FileUploadS3";
import NoticeService from "../../../../services/NoticeService";
import { getErrorMessage } from "../../../../helpers/functions";
import * as XLSX from "xlsx";

const validFormat = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const FileTracking = ({ modal, setModal, MTRef, userObject }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const [contractAwbMap, setContractAwbMap] = useState({});

  const normalizeHeader = (text = "") =>
    text.toString().toLowerCase().replace(/[\s_]/g, "");

  const findColumnValue = (row, expectedName) => {
    const normalizedExpected = normalizeHeader(expectedName);

    const key = Object.keys(row).find(
      (k) => normalizeHeader(k) === normalizedExpected,
    );

    return key ? row[key] : null;
  };

  const handleExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const rows = XLSX.utils.sheet_to_json(sheet);

          if (!rows.length) {
            enqueueSnackbar("Excel file is empty", { variant: "error" });
            return reject();
          }
          const firstRow = rows[0];
          const contractTest = findColumnValue(firstRow, "CONTRACT NUMBER");
          const awbTest = findColumnValue(firstRow, "AWB no");
          const noticeTest = findColumnValue(firstRow, "Notice Category");

          if (!contractTest || !awbTest || !noticeTest) {
            enqueueSnackbar(
              "Excel must contain columns: CONTRACT NUMBER, AWB no, Notice Category",
              { variant: "error" },
            );
            return reject();
          }

          const map = {};

          rows.forEach((row) => {
            const contractNo = findColumnValue(row, "CONTRACT NUMBER");
            const awbNo = findColumnValue(row, "AWB no");
            const noticeName = findColumnValue(row, "Notice Category");

            if (!contractNo || !awbNo) return;

            if (!map[contractNo]) {
              map[contractNo] = [];
            }

            const awbAlreadyExists = map[contractNo].some((item) =>
              item.endsWith(`(${awbNo})`),
            );

            if (!awbAlreadyExists) {
              const value = `${noticeName || "Notice"} (${awbNo})`;
              map[contractNo].push(value);
            }
          });

          setContractAwbMap(map);
          resolve();
        } catch (error) {
          enqueueSnackbar("Invalid Excel format", { variant: "error" });
          reject(error);
        }
      };

      reader.onerror = () => {
        enqueueSnackbar("Failed to read file", { variant: "error" });
        reject();
      };

      reader.readAsBinaryString(file);
    });
  };

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "please wait..." });

      const payload = {
        uploader_name: userObject?.name,
        uploader_email: userObject?.email,
        tracking_file: values?.tracking_file,
        contract_awb_map: contractAwbMap,
      };

      const response = await NoticeService.addNotice(payload);

      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (err) {
      const error = getErrorMessage(err);
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange(MTRef.current.state?.query);
      setModal({ state: false });
    }
  }

  return (
    <Modal open={modal?.state} onClose={() => setModal({ state: false })}>
      <DrawerContainer role="presentation">
        <HeadingContainer>
          <Heading>Notice Tracking</Heading>
          <CloseModal
            onClick={() => setModal({ state: false })}
            src={require("../../../../assets/images/closeModal.svg")}
          />
        </HeadingContainer>

        <FormContainer>
          <Formik
            initialValues={{ tracking_file: "" }}
            enableReinitialize
            validateOnBlur
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({ values, handleSubmit, setFieldValue }) => (
              <>
                <StyledFormElementsWrapper>
                  <div style={{ marginBottom: 13 }}>
                    <CustomFileUpload
                      setFile={async (file) => {
                        await handleExcelFile(file);
                        s3Upload(
                          file,
                          "tracking_file",
                          setFieldValue,
                          validFormat,
                          setLoader,
                        );
                      }}
                      style={{ marginTop: 10, width: "100%" }}
                      accept=".xlsx"
                      file={{ name: getFileName(values?.tracking_file) }}
                      fileUrl={values?.tracking_file}
                    />
                  </div>
                </StyledFormElementsWrapper>

                <Flex>
                  <HalfWidth>
                    <PrimaryOutlinedCloseCTAButton
                      onClick={() => setModal({ state: false })}
                    >
                      Cancel
                    </PrimaryOutlinedCloseCTAButton>
                  </HalfWidth>

                  <HalfWidth>
                    <PrimaryCTAButton
                      disabled={!values.tracking_file}
                      onClick={handleSubmit}
                    >
                      Submit
                    </PrimaryCTAButton>
                  </HalfWidth>
                </Flex>
              </>
            )}
          </Formik>
        </FormContainer>
      </DrawerContainer>
    </Modal>
  );
};

export default FileTracking;

export const DrawerContainer = styled.div`
  max-width: 95%;
  width: 100%;
  background-color: white;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 8px;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: ${({ width }) => (width ? width : "516px")};
  }
`;

export const HeadingContainer = styled.div`
  padding-top: 30px;
  padding-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  right: 30px;
`;

export const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

export const FormContainer = styled.div`
  padding-top: 14px;
  padding-left: 54px;
  padding-right: 54px;
  padding-bottom: 26px;
  & form {
    padding-top: 10px;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 47px;
`;

const HalfWidth = styled.div`
  width: 48%;
`;

const StyledFormElementsWrapper = styled.div`
  margin-bottom: 28px;
  max-width: 436px;
  & .link {
    margin-top: 10px;
    display: flex;
    justify-content: flex-start;
  }
  & .template {
    margin-top: 0px;
  }
  & .waring-msg {
    color: ${COLORS.WARNING_COLOR};
    font-size: 10px;
    white-space: nowrap;
  }
  & .mediator-type {
    .MuiFormGroup-root {
      display: flex;
      flex-direction: column !important;
      & .MuiFormControlLabel-root {
        padding: 10px 0px;
      }
    }
  }
  @media ${theme?.breakpoints?.sm_down} {
    & .template {
      margin-top: 15px;
    }
  }
`;
